import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {Alert, Stack} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';

import {Profile} from '../profile/Profile';
import {FormDrawer} from '../drawers/FormDrawer';

import {EditPageNavigation} from '../navigation/EditPageNavigation';

import {useEditPageTools} from '../hooks/useEditPageTools';
import {useProduct} from '../hooks/useProduct.ts';
import {Product} from '../types';

export const EditPage = () => {

    const {id: productId} = useParams();

    if (typeof productId === 'undefined') {
        throw new Error('productId is required parameter');
    }

    const {record: product, loading, isError, updateRecord} = useProduct(productId);

    const [drawerOpened, setDrawerOpened] = useState(false);

    const {tools} = useEditPageTools({onEdit: () => setDrawerOpened(true)});

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading product...Please wait')}/>;
    }

    // A failed fetch, or an id that resolves to nothing (deleted, mistyped), used to spin
    // the loader forever.
    if (isError || !product) {
        return (
            <Alert color='red' title={I18n.get('Could not load this product')} m='md'>
                {isError
                    ? I18n.get('The billing API did not return the product. Try reloading the page.')
                    : `${I18n.get('No product has the id')} ${productId}. ${I18n.get('It may have been deleted.')}`}
            </Alert>
        );
    }

    // Resolves true only when the update landed; FormDrawer closes on that alone.
    const handleDrawerSubmit = (values: Product) => updateRecord(values);

    return (
        <FormPageLayout
            title={product.name ? product.name : product.id}
            pageNavigation={<EditPageNavigation component={product}/>}
            tools={tools}
        >
            <Stack gap='lg'>
                <Profile record={product}/>
            </Stack>

            <FormDrawer
                record={product}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </FormPageLayout>
    );
};
